import apiClient from "./apiClient";
import { Vehicle, VehicleListItem, VehicleSearch } from "vehicles/models";
import { PagedResults } from "common/models";
import { VehicleHistorySearch } from "map/model";
import { toast } from "react-toastify";
import axios, { CancelToken } from "axios";

const searchVehicles = async (search: VehicleSearch | null): Promise<PagedResults<VehicleListItem>> => {
  let uri = "/vehicles/search";
  var query: string[] = [];
  if (search?.hubId) query.push(`hubId=${search.hubId}`);
  if (search?.unitNumber) query.push(`unitNumber=${search.unitNumber}`);
  if (search?.typeId) query.push(`typeId=${search.typeId}`);
  if (search?.statusId) query.push(`statusId=${search.statusId}`);
  if (search?.includeInactive) query.push(`includeInactive=true`);
  query.push(`offset=${search?.pagedSearchOptions.offset}`);
  query.push(`pageSize=${search?.pagedSearchOptions.pageSize}`);
  query.push(`sortName=${search?.sortName}`);
  query.push(`sortAsc=${search?.sortAsc}`);
  if (query.length > 0) uri = `${uri}?${query.join("&")}`;

  return apiClient.get(uri);
};

const getVehiclesByHub = async (hubId: number): Promise<VehicleListItem[]> => {
  let uri = `/vehicles/hub/${hubId}`;
  return apiClient.get(uri);
};

const getVehicle = async (id: number): Promise<Vehicle> => {
  let uri = `/vehicles/${id}`;
  return apiClient.get(uri);
};

const saveVehicle = async (vehicle: Vehicle): Promise<any> => {
  return vehicle.id
    ? apiClient.put(`/vehicles/${vehicle.id}`, vehicle).then(v => toast.success("Vehicle Saved."))
      .catch((e) => toast.error("Error Saving Vehicle"))
    : apiClient.post(`/vehicles/`, vehicle).then(v => toast.success("Vehicle Created."))
      .catch((e) => toast.error("Error Saving Vehicle"));
};

const getVehicleLocationByHub = async (id: number): Promise<any> => {
  let uri = "/vehicles/locations-hub/" + (id ? id : "");
  return apiClient.get(uri)
  .catch((e) => toast.error("Error Retriving Vehicle"));
};

const getVehicleLocationByUnitNumber = async (unitNumber: string): Promise<any> =>{
  let uri = `/vehicles/locations-unitnumber/${unitNumber}`;
  return apiClient.get(uri).catch((e) => toast.error("Error searching vehicle."));
};

const getVehicleLocationHistory = async (request: VehicleHistorySearch): Promise<any> => {
  return apiClient.post("/vehicles/history", {
    ...request,
    startDate: new Date(request.startDate).toISOString(),
    endDate: new Date(request.endDate).toISOString(),
  }).catch((e) => toast.error("Error getting vehicle history."));;
};

const getVehicleActivitySummary = async (request: VehicleHistorySearch, cancelToken?: CancelToken): Promise<any> => {
  return apiClient.post("/vehicles/activity-summary", {
    ...request,
    startDate: new Date(request.startDate).toISOString(),
    endDate: new Date(request.endDate).toISOString(),
  }, cancelToken).catch((e) => {
    if (axios.isCancel(e)) return; // Request was cancelled
    toast.error("Error getting Vehicle summary.");
  });
};

const getVehicleActivity = async (request: VehicleHistorySearch): Promise<any> => {
  if (!request.unitNumber || !request.startDate || !request.endDate) return;

  let uri = `/vehicles/activity?unitNumber=${request.unitNumber}&startDate=${new Date(
    request.startDate
  ).toISOString()}&endDate=${new Date(request.endDate).toISOString()}`;
  return apiClient.get(uri).catch((e) => toast.error("Error getting vehicle activity."));
};

const searchVehicleUnitNumber = async (unitNumber: string): Promise<any> => {
  let uri = `/lookup/vehicles/unit-number-search?unitNumber=${unitNumber}`;
  return apiClient.get(uri).catch((e) => toast.error("Error searching vehicle."));
};

const getVehicleClientPoints = async (unitNumber: number, clientName: string,  startDate: any, endDate: any, ): Promise<any> =>{
  return apiClient.get(`/vehicles/${unitNumber}/client/${clientName}/points?startDate=${startDate}&endDate=${endDate}`);
}

const getEcmOdometer = async (unitNumber: number): Promise<EcmOdometer | null> => {
  return apiClient
    .get<undefined, EcmOdometer | null>(`/vehicles/${unitNumber}/ecm-odometer`)
    .catch(() => null);
};

const getVehicleHistory = async (id: number): Promise<VehicleHistorySnapshot[]> => {
  return apiClient
    .get<undefined, VehicleHistorySnapshot[]>(`/vehicles/${id}/history`)
    .catch(() => [] as VehicleHistorySnapshot[]);
};

const vehiclesService = {
  searchVehicles,
  getVehiclesByHub,
  getVehicle,
  saveVehicle,
  getVehicleLocationByHub,
  getVehicleLocationHistory,
  getVehicleActivitySummary,
  getVehicleActivity,
  searchVehicleUnitNumber,
  getVehicleLocationByUnitNumber,
  getVehicleClientPoints,
  getEcmOdometer,
  getVehicleHistory,
};

export interface EcmOdometer {
  odometerValue: number | null;
  odometerDateTimeUTC: string | null;
  odometerSource: string | null;
}

export interface VehicleHistorySnapshot {
  auditId: number | null;
  isCurrent: boolean;
  timestamp: string | null;
  userId: number | null;
  userName: string | null;
  auditAction: string | null;
  hubId: number | null;
  vehicleTypeId: number | null;
  description: string | null;
  unitNumber: number;
  plateNumber: string | null;
  vin: string | null;
  odometer: number;
  isActive: boolean;
  isSuspended: boolean;
  vehicleStatus: string | null;
  outOfServiceReason: string | null;
  capacity: string | null;
  make: string | null;
  year: string | null;
  color: string | null;
  phoneInVehicle: string | null;
  isSatelliteEquipped: boolean | null;
  modemIpAddress: string | null;
  modemEsnNumber: string | null;
  deviceSerialNumber: string | null;
  cameraDeviceNumber: string | null;
  modemSimNumber: string | null;
  fuelTankVolume: number | null;
  leaseCompany: string | null;
  comments: string | null;
}

export default vehiclesService;
